/* Admin push notifications — client helper
 * ----------------------------------------
 * Registers this device (the admin's phone/browser) to receive a Firebase
 * Cloud Messaging push whenever a new reservation request is created.
 *
 * Flow:
 *   1. Ask the browser for Notification permission.
 *   2. Register the background service worker (/firebase-messaging-sw.js).
 *   3. Get an FCM token for this device and save it to Firestore
 *      (collection "adminDevices"), where the Cloud Function reads it.
 *
 * Needs REACT_APP_FIREBASE_VAPID_KEY in the site's .env (the "Web Push
 * certificate" key pair from Firebase Console → Cloud Messaging).
 */
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { app, db } from "./firebaseConfig";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// True on browsers/devices that can actually receive web push
// (Android Chrome, desktop Chrome/Edge/Firefox, installed PWAs).
export async function pushSupported() {
  try {
    return (
      "serviceWorker" in navigator &&
      "Notification" in window &&
      (await isSupported())
    );
  } catch {
    return false;
  }
}

// Reflects the current state without prompting: "on" | "denied" | "off".
export function currentPushPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "on";
  if (Notification.permission === "denied") return "denied";
  return "off";
}

// Turns on push for THIS device. Resolves with the token, or throws a code:
//   "no-vapid" | "unsupported" | "denied" | "no-token"
export async function enableAdminPush(uid) {
  if (!VAPID_KEY) throw new Error("no-vapid");
  if (!(await pushSupported())) throw new Error("unsupported");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("denied");

  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js"
  );

  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error("no-token");

  // Doc id == token so re-enabling the same device is idempotent.
  await setDoc(
    doc(db, "adminDevices", token),
    {
      token,
      uid: uid || null,
      ua: navigator.userAgent,
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );

  return token;
}

// Optional: show a lightweight in-app notice when a push arrives while the
// admin already has the dashboard open in the foreground.
export function listenForegroundPush(handler) {
  (async () => {
    if (!(await pushSupported())) return;
    try {
      const messaging = getMessaging(app);
      onMessage(messaging, (payload) => handler && handler(payload));
    } catch {
      /* no-op */
    }
  })();
}
